import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';

import { Network, SharesItemFragment } from 'api';
import { ContentProvider } from 'providers';
import {
  DataTable,
  DataTableActions,
  DataTableColumn,
  EmptyState,
  Numeric,
  PostImage,
  SocialIcon,
} from 'components';
import { useNotify, useReleases } from 'hooks';
import { ShareButton } from 'features/content/widgets';
import { useSharesItems } from 'features/activity/hooks';
import { IMAGE_HEIGHT } from 'features/activity/constants';

import ListLoader from './ListLoader';
import DateCell from './Date';
import PostCell from './Post';
import ShareCopyCell from './ShareCopy';

const PastShares: FC = () => {
  const notify = useNotify();
  const { t } = useTranslation();
  const releases = useReleases();

  const hasImpressions = releases.includes('impressions');

  const { results, isExhausted, isEmpty, isInitialLoad, sentinelRef } = useSharesItems(
    {
      scheduled: false,
      data: {
        limit: 10,
      },
    },
    {
      onError: notify.queryError,
    }
  );

  if (isInitialLoad) {
    return <ListLoader />;
  }

  if (isEmpty) {
    return <EmptyState name="past" />;
  }

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  const columns: DataTableColumn<SharesItemFragment>[] = [
    {
      width: 170,
      headerCell: {
        render: t('common:shared'),
      },
      bodyCell: {
        render: shareItem => <DateCell time={shareItem.shared_at} />,
      },
    },
    {
      width: IMAGE_HEIGHT + 16,
      headerCell: {
        padding: 'dense',
        render: t('common:post'),
      },
      bodyCell: {
        padding: 'dense',
        render: shareItem => <PostImage size={IMAGE_HEIGHT} content={shareItem.content} />,
      },
    },
    {
      width: 300,
      headerCell: {
        render: null,
      },
      bodyCell: {
        render: shareItem => <PostCell content={shareItem.content} />,
      },
    },
    {
      width: 74,
      headerCell: {
        render: t('common:network'),
      },
      bodyCell: {
        render: shareItem => (
          <Grid container justify="center">
            <Grid item>
              <SocialIcon provider={shareItem.network as Network} />
            </Grid>
          </Grid>
        ),
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('components:shareCopy'),
      },
      bodyCell: {
        render: shareItem => <ShareCopyCell item={shareItem} />,
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:click_plural'),
      },
      bodyCell: {
        render: shareItem => <Numeric value={shareItem.clicks} />,
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:engagement_plural'),
      },
      bodyCell: {
        render: shareItem => <Numeric value={shareItem.engagements} />,
      },
    },
  ];

  if (hasImpressions) {
    columns.splice(5, 0, {
      width: 100,
      headerCell: {
        render: t('common:impression_plural'),
      },
      bodyCell: {
        render: shareItem => <Numeric value={shareItem.impressions} />,
      },
    });
  }

  const actions: DataTableActions<SharesItemFragment> = shareItem => {
    return (
      <Grid wrap="nowrap" container alignItems="center" justify="flex-end">
        <Grid item>
          <ContentProvider value={shareItem.content}>
            <ShareButton size="medium" />
          </ContentProvider>
        </Grid>
      </Grid>
    );
  };

  return (
    <DataTable
      data={results}
      columns={columns}
      actions={actions}
      pagination={pagination}
      rowSelection={null}
    />
  );
};

export default PastShares;
